import { useState } from "preact/hooks";
import {
  audioUrl,
  categoryTitle,
  deleteMessage,
  editMessage,
  type Library,
  type Message,
  saveCategory,
  type VoiceOption,
} from "./api.ts";
import { Close, Keyboard, Microphone, Play, Plus } from "./icons.tsx";
import { OverflowMenu } from "./menu.tsx";
import { cacheDelay } from "./phrases.ts";
import {
  Button,
  Callout,
  Card,
  Confirm,
  DigitCircle,
  Field,
  Pill,
  Row,
  ScreenHead,
  ScreenTitle,
} from "./ui.tsx";
import { useAction } from "./use-action.ts";
import { voiceNameById } from "./voices.ts";

/** The message a save just added, so the row it landed in can say so. */
export interface Saved {
  category: string;
  id: string;
}

interface Props {
  library: Library;
  /** Every voice the engine offers, for naming the one a typed message used. */
  voices: VoiceOption[];
  digit: string;
  category: string;
  saved: Saved | null;
  onDismissSaved: () => void;
  onBack: () => void;
  onAdd: () => void;
  /** Reopens a typed message on the screen that wrote it. */
  onEdit: (message: Message) => void;
  reload: () => Promise<void>;
}

/** What the page is playing, if anything. */
interface Playing {
  what: string;
  url: string;
}

export function NumberScreen(props: Props) {
  const { library, voices, digit, category, saved, onBack, onAdd, onEdit, reload } =
    props;
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renamingMessage, setRenamingMessage] = useState<Renaming | null>(null);
  const [deleting, setDeleting] = useState<Message | null>(null);
  const [playing, setPlaying] = useState<Playing | null>(null);
  const { busy, error, setError, run } = useAction();

  const manifest = library.categories.find((entry) => entry.category === category);
  const title = categoryTitle(library, category);
  const enabled = manifest?.enabled !== false;

  const messages = [...(manifest?.messages ?? [])].sort((a, b) => {
    return b.created.localeCompare(a.created);
  });

  function edit(change: () => Promise<unknown>): Promise<boolean> {
    return run(async () => {
      await change();
      await reload();
    });
  }

  return (
    <>
      <ScreenHead
        label="All numbers"
        onBack={onBack}
        trailing={
          <OverflowMenu
            label={`More for ${title}`}
            items={[
              { label: "Rename", onSelect: () => setRenaming(title) },
              {
                // No confirm: nothing is deleted and nothing moves, and one
                // tap puts the number back.
                label: enabled ? "Disable this number" : "Enable this number",
                onSelect: () => {
                  void edit(() => saveCategory(category, title, !enabled));
                },
              },
            ]}
          />
        }
      />

      <div class="number-head">
        <DigitCircle digit={digit} size="sm" />
        {renaming === null ? (
          <ScreenTitle>{title}</ScreenTitle>
        ) : (
          <RenameInPlace
            id="rename-topic"
            label="Topic name"
            busy={busy}
            rename={{
              value: renaming,
              onInput: setRenaming,
              onSave: () => {
                void edit(() => saveCategory(category, renaming.trim())).then((ok) => {
                  if (ok) setRenaming(null);
                });
              },
              onCancel: () => setRenaming(null),
            }}
          />
        )}
      </div>
      <p class="lede">
        {enabled
          ? `Callers who dial ${digit} hear one of these.`
          : "This number is off. Callers hear the not-in-service message."}
      </p>

      {playing && (
        <div class="player">
          <span class="player-what">Playing {playing.what}</span>
          {/* biome-ignore lint/a11y/useMediaCaption: a stored voice message has no caption track */}
          <audio controls autoPlay src={playing.url} />
          <button
            type="button"
            class="icon-btn"
            aria-label="Stop playing"
            onClick={() => setPlaying(null)}
          >
            <Close />
          </button>
        </div>
      )}

      {error && <Callout tone="bad" title={error} onDismiss={() => setError(null)} />}

      {deleting && (
        <Confirm
          title={`Delete "${deleting.title}"?`}
          body="This deletes the message. It cannot be undone."
          confirmLabel="Delete"
          busy={busy}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            // Closed whether or not it worked: a refusal is a `Callout` on the
            // page, which nobody can read through the dialog's own backdrop.
            void edit(() => deleteMessage(category, deleting.id)).then(() => {
              setDeleting(null);
            });
          }}
        />
      )}

      {saved?.category === category && (
        <Callout tone="good" title="Saved" onDismiss={props.onDismissSaved}>
          The phone should pick it up within {cacheDelay()}.
        </Callout>
      )}

      <Button full onClick={onAdd}>
        <Plus />
        <span>Add message</span>
      </Button>

      {messages.length === 0 ? (
        <p class="empty">
          No messages yet. Add one and callers will hear it on this number.
        </p>
      ) : (
        <Card class="card-rows">
          {messages.map((entry) => {
            const draft = renamingMessage?.id === entry.id ? renamingMessage : null;
            return (
              <MessageRow
                key={entry.id}
                message={entry}
                voices={voices}
                fresh={saved?.category === category && saved.id === entry.id}
                busy={busy}
                onListen={() => {
                  void run(async () => {
                    const link = await audioUrl(category, entry.id, "master");
                    setPlaying({ what: entry.title, url: link.url });
                  });
                }}
                onToggle={() => {
                  void edit(() =>
                    editMessage(category, entry.id, { active: !entry.active }),
                  );
                }}
                onEdit={() => onEdit(entry)}
                onRename={() =>
                  setRenamingMessage({ id: entry.id, title: entry.title })
                }
                rename={
                  draft === null
                    ? undefined
                    : {
                        value: draft.title,
                        onInput: (value) =>
                          setRenamingMessage({ id: entry.id, title: value }),
                        onSave: () => {
                          void edit(() =>
                            editMessage(category, entry.id, {
                              title: draft.title.trim(),
                            }),
                          ).then((ok) => {
                            if (ok) setRenamingMessage(null);
                          });
                        },
                        onCancel: () => setRenamingMessage(null),
                      }
                }
                onDelete={() => setDeleting(entry)}
              />
            );
          })}
        </Card>
      )}
    </>
  );
}

/** A row title being changed in place: which row, and the draft so far. */
interface Renaming {
  id: string;
  title: string;
}

/** A title becoming a text field in place: the draft, and the two ways out. */
interface Rename {
  value: string;
  onInput: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

interface RowProps {
  message: Message;
  /** The account's voices, for naming the one a typed message was read in. */
  voices: VoiceOption[];
  /** Saved in this session, so it is worth pointing at. */
  fresh: boolean;
  busy: boolean;
  /** Present while this row's title is a text field, absent the rest of the time. */
  rename?: Rename | undefined;
  onListen: () => void;
  onToggle: () => void;
  onEdit: () => void;
  onRename: () => void;
  onDelete: () => void;
}

function MessageRow({
  message,
  voices,
  fresh,
  busy,
  rename,
  onListen,
  onToggle,
  onEdit,
  onRename,
  onDelete,
}: RowProps) {
  const typed = message.source === "tts";
  const read = message.voice ? voiceNameById(voices, message.voice) : null;
  return (
    <Row
      class={fresh ? "row-message row-fresh" : "row-message"}
      title={
        rename ? (
          <RenameInPlace
            id="rename-message"
            label="Message title"
            busy={busy}
            rename={rename}
          />
        ) : (
          message.title
        )
      }
      meta={
        <>
          <span class="source">
            {typed ? <Keyboard /> : <Microphone />}
            {typed ? (read ? `Typed · ${read}` : "Typed") : "Recorded"}
          </span>
          <Pill tone={message.active ? "enabled" : "disabled"} />
        </>
      }
      body={message.text ? <p class="spoken">{message.text}</p> : undefined}
      trailing={
        rename ? undefined : (
          <>
            <Button disabled={busy} onClick={onListen}>
              <Play />
              <span>Listen</span>
            </Button>
            {fresh && !message.active ? (
              <Button tone="primary" disabled={busy} onClick={onToggle}>
                Enable
              </Button>
            ) : (
              <OverflowMenu
                label={`More for ${message.title}`}
                items={[
                  // A recording's audio cannot change, so renaming is the whole
                  // of its edit. A typed message reopens on the screen that
                  // wrote it, where the title is one of three things to change.
                  typed
                    ? { label: "Edit", onSelect: onEdit }
                    : { label: "Rename", onSelect: onRename },
                  { label: message.active ? "Disable" : "Enable", onSelect: onToggle },
                  { label: "Delete", destructive: true, onSelect: onDelete },
                ]}
              />
            )}
          </>
        )
      }
    />
  );
}

/**
 * The topic in the header and a message's title in its row are renamed the
 * same way: the title becomes a text field with Save and Cancel under it. Both
 * can be open at once, so each names its own control.
 */
function RenameInPlace({
  id,
  label,
  busy,
  rename,
}: {
  id: string;
  label: string;
  busy: boolean;
  rename: Rename;
}) {
  return (
    <div class="rename">
      <Field id={id} label={label} hideLabel>
        <input
          id={id}
          type="text"
          value={rename.value}
          disabled={busy}
          onInput={(event) => rename.onInput((event.target as HTMLInputElement).value)}
        />
      </Field>
      <div class="rename-actions">
        <Button tone="quiet" disabled={busy} onClick={rename.onCancel}>
          Cancel
        </Button>
        <Button
          tone="primary"
          busy={busy}
          busyLabel="Saving…"
          disabled={rename.value.trim() === ""}
          onClick={rename.onSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
